import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import EnterpiseTeam from 'components/EnterpiseTeam';
import EnterpriseSuggestedActions from 'components/EnterpriseSuggestedActions';
import useBulkUploadReviewState from 'hooks/useBulkUploadReviewState';
import BulkAddDataRooms from '../BulkAddDataRooms';
import EnterpriseDataRooms from '../EnterpriseDataRooms';
import { DataRoomSheet } from 'router/subrouters/DataRoom/models/DataRoom';
import BulkUploadDataRooms from '../BulkUploadDataRooms';
import { addMissingRequiredFieldsToDataRoom } from '../BulkAddDataRooms/components/utils';
import useRefetch from 'hooks/useRefetch';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import { LISTEN_EVENTS } from 'services/socketService/socketEventNames';
import { DataRoomUploadedEventData } from 'services/socketService/socketEventData';
import EnterpriseContext from '../../providers/Enterprise/Enterprise.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './RwcAnalyticsLayout.styles.scss';
import './RwcAnalyticsLayout.styles.responsive.scss';

type RwcAnalyticsLayoutProps = {
  className?: string;
};

const RwcAnalyticsLayout: React.FC<RwcAnalyticsLayoutProps> = (props) => {
  const { className } = props;

  const { isOfficeManager } = useContext(CurrentUserContext);
  const { enterprise, updateBusinessUnits } = useContext(EnterpriseContext);

  const { refetchFlag, refetch } = useRefetch();

  const classes = classNames('rwc-analytics-layout', className);
  const {
    columns,
    rows: draftDataRooms,
    editRow: editDataRoom,
    onUpdateColumn,
    handleUploadRows,
    clearRows: clearDataRooms,
  } = useBulkUploadReviewState<DataRoomSheet>({
    addMissingRequiredFields: addMissingRequiredFieldsToDataRoom,
  });

  const isBulkUploading = useMemo(
    () => !!draftDataRooms?.length,
    [draftDataRooms?.length],
  );

  const drBulkCreated = useCallback(
    ({ enterpriseId: payloadEnterpriseId }: DataRoomUploadedEventData) => {
      if (enterprise._id !== payloadEnterpriseId) return;

      refetch();
    },
    [enterprise, refetch],
  );

  useSocketEvent(LISTEN_EVENTS.DR_BULK_CREATED, drBulkCreated);

  return (
    <div className={classes}>
      {isBulkUploading && (
        <BulkAddDataRooms
          enterpriseId={enterprise._id}
          draftDataRooms={draftDataRooms}
          columns={columns}
          onUpdateColumn={onUpdateColumn}
          editDataRoom={editDataRoom}
          clearDataRooms={clearDataRooms}
        />
      )}

      <div
        className={classNames('content-wrapper', {
          hidden: isBulkUploading,
        })}
      >
        {!isOfficeManager && (
          <>
            <div className="rwc-analytics-layout__cards">
              <EnterpriseDataRooms
                enterprise={enterprise}
                refetchFlag={refetchFlag}
              />
              <BulkUploadDataRooms onUpload={handleUploadRows} />
            </div>
            <EnterpriseSuggestedActions enterpriseId={enterprise._id} />
          </>
        )}

        <EnterpiseTeam
          enterprise={enterprise}
          refetchFlag={refetchFlag}
          withNavigation={false}
          isRWC
          updateBusinessUnits={updateBusinessUnits}
        />
      </div>
    </div>
  );
};

export default RwcAnalyticsLayout;
